import { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, Building2, Link as LinkIcon, Check, MapPin, Clock, Copy, User, Calendar, Lock } from 'lucide-react';
import { Button } from './Button';
import { PaymentSuccessModal } from './checkout/PaymentSuccessModal';
import { useBooking } from '../hooks/useBooking';
import { useNavigation } from '../hooks/useNavigation';
import { useTicketStore } from '../store/ticketStore';
import { TicketData } from '../models/Ticket';

type PaymentMethod = 'spei' | 'card' | 'link' | null;

export const PaymentForm = () => {
  console.log('🔷 PAYMENT_FORM_RENDER_START');
  
  // Obtener datos del store en lugar de props
  const { selectedBooking, passengerData } = useBooking();
  const { goToSearch, goToPassengers, goToTicket, goToHome } = useNavigation();
  const setSelectedTicket = useTicketStore((state) => state.setSelectedTicket);

  console.log('🔷 PAYMENT_FORM_BOOKING_DATA', {
    hasSelectedBooking: !!selectedBooking,
    selectedBooking: selectedBooking,
    hasBookingData: !!selectedBooking?.selectedSeats,
    hasSeatSelection: !!selectedBooking?.selectedSeats,
    seatCount: selectedBooking?.selectedSeats?.length ?? 0,
  });

  console.log('🔷 PAYMENT_FORM_PASSENGER_DATA', {
    hasPassengerData: !!passengerData,
    passengerData: passengerData,
    isArray: Array.isArray(passengerData),
    passengerCount: passengerData?.length ?? 0,
  });

  // Validar que hay datos con useEffect
  useEffect(() => {
    console.log('🔷 PAYMENT_FORM_EFFECT_CHECK', {
      hasSelectedBooking: !!selectedBooking,
      hasPassengerData: !!passengerData,
      passengerDataLength: passengerData?.length ?? 0,
    });
    
    if (!selectedBooking) {
      console.warn('⚠️ PAYMENT_FORM_REDIRECT_NO_BOOKING');
      goToSearch();
    } else if (!passengerData || passengerData.length === 0) {
      console.warn('⚠️ PAYMENT_FORM_REDIRECT_NO_PASSENGERS', {
        passengerData: passengerData,
        length: passengerData?.length ?? 0,
      });
      goToPassengers();
    } else {
      console.log('✅ PAYMENT_FORM_VALIDATION_PASSED');
    }
  }, [selectedBooking, passengerData, goToSearch, goToPassengers]);

  // 🔧 PREVIEW/REAL APP COMPATIBILITY FIX  
  // In preview: selectedBooking exists directly (no nested bookingData)
  // In real app: selectedBooking.bookingData exists
  // This makes it work in both environments without breaking either
  const hasAnyBookingData = selectedBooking && (selectedBooking.selectedSeats || selectedBooking);
  
  console.log('🔧 ENVIRONMENT_CHECK', {
    hasSelectedBooking: !!selectedBooking,
    hasNestedBookingData: !!selectedBooking?.selectedSeats,
    hasAnyBookingData: !!hasAnyBookingData,
    environment: selectedBooking?.selectedSeats ? 'REAL_APP' : 'PREVIEW',
  });

  // Si no hay ningún tipo de booking data, mostrar loading mientras redirige
  if (!hasAnyBookingData) {
    console.warn('⚠️ PAYMENT_FORM_LOADING_STATE_NO_BOOKING', {
      hasSelectedBooking: !!selectedBooking,
      hasBookingData: !!selectedBooking,
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280] font-medium">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  // Verificación adicional de passengerData antes de renderizar
  if (!passengerData || passengerData.length === 0) {
    console.warn('⚠️ PAYMENT_FORM_LOADING_STATE_NO_PASSENGERS', {
      passengerData: passengerData,
      length: passengerData?.length ?? 0,
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280] font-medium">Redirigiendo a pasajeros...</p>
        </div>
      </div>
    );
  }

  console.log('✅ PAYMENT_FORM_RENDER_CONTINUE');

  // 🔧 EFFECTIVE BOOKING DATA - Compatible with both environments
  // Real app: use bookingData from selectedBooking.bookingData
  // Preview: use selectedBooking directly as fallback
  const effectiveBookingData = selectedBooking?.selectedSeats ?? selectedBooking;
  const effectiveSelectedSeats = selectedBooking?.selectedSeats ?? (selectedBooking as any)?.seats ?? [];

  type BookingViewModel = {
  company: string;
  rating: number;
  ratingCount: number;
  price: number;
  departureTime: string;
  departureLocation: string;
  arrivalTime: string;
  arrivalLocation: string;
  duration: string;
  amenities: any[];
  availableSeats: number;
  discount?: any;
  vehicleType: string;
};

const firstWord = (s?: string) => (s ?? '').trim().split(/\s+/)[0] || '';

  // 🛡️ Defensive Guards with defaults
const safeBookingData: BookingViewModel =
  (effectiveBookingData as any) ?? {
    company: 'Sin nombre',
    rating: 0,
    ratingCount: 0,
    price: 0,
    departureTime: '--:--',
    departureLocation: 'Sin ubicación',
    arrivalTime: '--:--',
    arrivalLocation: 'Sin ubicación',
    duration: '0h',
    amenities: [],
    availableSeats: 0,
    discount: undefined,
    vehicleType: 'Bus',
  };

  const safeSelectedSeats = effectiveSelectedSeats ?? [];
  const safePassengerData = passengerData ?? [];

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  
  // Card form data
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  
const unitPrice =
  typeof safeBookingData?.price === 'number'
    ? safeBookingData.price
    : Number(String(safeBookingData?.price ?? '').replace(/[^\d.]/g, ''));

const seatsCount = Array.isArray(safeSelectedSeats) ? safeSelectedSeats.length : 0;

const discountValue = Number.isFinite(discount) ? discount : 0;

const totalPrice = (Number.isFinite(unitPrice) ? unitPrice : 0) * seatsCount;
const finalPrice = Math.max(totalPrice - discountValue, 0);


  const handleMethodSelect = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setIsLoading(true);
    setShowPaymentDetails(false);
    
    setTimeout(() => {
      setIsLoading(false);
      setShowPaymentDetails(true);
    }, 1000);
  };

  const handleApplyCoupon = () => {
    if (couponCode.toUpperCase() === 'MEXMO10') {
      setDiscount(totalPrice * 0.1);
      setCouponApplied(true);
    }
  };

  const handlePayment = () => {
    // Crear el ticket con los datos de la reserva
    const ticket: TicketData = {
      id: `TKT-${Date.now()}`,
      ticketNumber: `MEXMO-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
      origin: safeBookingData.departureLocation,
      destination: safeBookingData.arrivalLocation,
      date: new Date().toLocaleDateString('es-MX'),
      time: safeBookingData.departureTime,
      busLine: safeBookingData.company,
      seatNumber: safeSelectedSeats.join(', '),
      passengerName: safePassengerData[0]?.firstName + ' ' + safePassengerData[0]?.lastName || 'Pasajero',
      price: finalPrice,
tabStatus: 'active',
status: selectedMethod === 'card' ? 'confirmed' : 'pending',

      paymentMethod: selectedMethod as 'card' | 'spei' | 'link',
      paymentStatus: selectedMethod === 'card' ? 'completed' : 'pending',
    };

    // Guardar el ticket en el store
    setSelectedTicket(ticket);
    
    // Mostrar modal de éxito
    setShowSuccess(true);
  };

  const handleViewTicket = () => {
    goToTicket();
  };

  const handleBackToHome = () => {
    goToHome();
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + '/' + v.slice(2, 4);
    }
    return v;
  };

  const steps = [
    { id: 'seats', label: 'Asientos de Ida', completed: true },
    { id: 'passengers', label: 'Pasajeros', completed: true },
    { id: 'payment', label: 'Pago', completed: false },
  ];

  return (
    <>
      {/* Success Modal */}
      {showSuccess && selectedMethod && (
        <PaymentSuccessModal
          paymentMethod={selectedMethod}
          onViewTicket={handleViewTicket}
          onBackToHome={handleBackToHome}
        />
      )}

      {/* Mobile View */}
      <div className="lg:hidden min-h-screen bg-[#F7F8FA]">
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-[#D97540] text-white shadow-md">
          <div className="flex items-center gap-3 p-4">
            <button
              onClick={goToPassengers}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-base">Método de pago</h1>
              <p className="text-xs opacity-90">
{firstWord(safeBookingData?.departureLocation) || 'Origen'} → {firstWord(safeBookingData?.arrivalLocation) || 'Destino'}
              </p>
            </div>
          </div>

          {/* Trip Info */}
          <div className="px-4 pb-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Tu viaje de ida</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{safeBookingData.departureTime} - {safeBookingData.arrivalTime}</span>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="px-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="text-sm font-semibold">Pago</span>
            </div>
            <span className="text-xs">Paso 3 de 3</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 pb-32">
          <h2 className="text-lg font-bold text-[#111827] mb-1">Selecciona tu método de pago</h2>
          <p className="text-sm text-[#6B7280] mb-6">Total: ${totalPrice.toLocaleString('es-MX')} MXN</p>

          {/* Add Coupon Section */}
          <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-4 mb-4">
            <h3 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-[#FF6A2A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Añadir cupón
            </h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                disabled={couponApplied}
                className="flex-1 px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent disabled:bg-[#F7F8FA] disabled:text-[#9CA3AF]"
                placeholder="Ingresa tu cupón"
              />
              <button
                onClick={handleApplyCoupon}
                disabled={couponApplied || !couponCode}
                className="px-4 py-2.5 bg-[#FF6A2A] hover:bg-[#C2410C] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white font-semibold rounded-[10px] text-sm transition-colors"
              >
                {couponApplied ? 'Aplicado' : 'Aplicar'}
              </button>
            </div>
            {couponApplied && (
              <div className="mt-3 p-2 bg-[#ECFDF5] border border-[#10B981] rounded-[8px] flex items-center gap-2">
                <Check className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#10B981] font-medium">
                  ¡Cupón aplicado! -${discount.toLocaleString('es-MX')} MXN
                </span>
              </div>
            )}
          </div>

          {/* Payment Methods */}
          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleMethodSelect('spei')}
              className={`w-full p-4 rounded-[12px] border-2 transition-all text-left ${
                selectedMethod === 'spei'
                  ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                  : 'border-[#E5E7EB] bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedMethod === 'spei' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                }`}>
                  <Building2 className={`w-5 h-5 ${
                    selectedMethod === 'spei' ? 'text-white' : 'text-[#6B7280]'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#111827]">Transferencia SPEI</h3>
                  <p className="text-xs text-[#6B7280]">Transferencia bancaria</p>
                </div>
                {selectedMethod === 'spei' && (
                  <Check className="w-5 h-5 text-[#FF6A2A]" />
                )}
              </div>
            </button>

            <button
              onClick={() => handleMethodSelect('card')}
              className={`w-full p-4 rounded-[12px] border-2 transition-all text-left ${
                selectedMethod === 'card'
                  ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                  : 'border-[#E5E7EB] bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedMethod === 'card' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                }`}>
                  <CreditCard className={`w-5 h-5 ${
                    selectedMethod === 'card' ? 'text-white' : 'text-[#6B7280]'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#111827]">Tarjeta de débito/crédito</h3>
                  <p className="text-xs text-[#6B7280]">Visa, Mastercard</p>
                </div>
                {selectedMethod === 'card' && (
                  <Check className="w-5 h-5 text-[#FF6A2A]" />
                )}
              </div>
            </button>

            <button
              onClick={() => handleMethodSelect('link')}
              className={`w-full p-4 rounded-[12px] border-2 transition-all text-left ${
                selectedMethod === 'link'
                  ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                  : 'border-[#E5E7EB] bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedMethod === 'link' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                }`}>
                  <LinkIcon className={`w-5 h-5 ${
                    selectedMethod === 'link' ? 'text-white' : 'text-[#6B7280]'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[#111827]">Link de pago</h3>
                  <p className="text-xs text-[#6B7280]">Paga en línea fácilmente</p>
                </div>
                {selectedMethod === 'link' && (
                  <Check className="w-5 h-5 text-[#FF6A2A]" />
                )}
              </div>
            </button>
          </div>

          {/* Payment Details */}
          {selectedMethod === 'spei' && (
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-4">
              {isLoading ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm text-[#6B7280]">Generando datos bancarios...</p>
                </div>
              ) : showPaymentDetails ? (
                <div>
                  <h3 className="font-bold text-[#111827] mb-4">Datos para transferencia</h3>
                  
                  <div className="space-y-4">
                    <div className="p-3 bg-[#F7F8FA] rounded-[10px]">
                      <p className="text-xs text-[#6B7280] mb-1">Beneficiario</p>
                      <p className="font-semibold text-[#111827]">Mexmo S.A. de C.V.</p>
                    </div>

                    <div className="p-3 bg-[#F7F8FA] rounded-[10px]">
                      <p className="text-xs text-[#6B7280] mb-1">Banco</p>
                      <p className="font-semibold text-[#111827]">BBVA México</p>
                    </div>

                    <div className="p-3 bg-[#F7F8FA] rounded-[10px]">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs text-[#6B7280] mb-1">CLABE</p>
                          <p className="font-mono font-bold text-[#111827]">012180001234567890</p>
                        </div>
                        <button className="p-2 hover:bg-[#E5E7EB] rounded-[8px] transition-colors">
                          <Copy className="w-4 h-4 text-[#6B7280]" />
                        </button>
                      </div>
                    </div>

                    <div className="p-3 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[10px]">
                      <p className="text-sm text-[#6B7280] mb-1">Monto a transferir</p>
                      <p className="text-3xl font-bold text-[#FF6A2A]">${finalPrice.toLocaleString('es-MX')} MXN</p>
                    </div>

                    <div className="p-3 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[10px]">
                      <p className="text-sm text-[#FF6A2A] font-semibold">
                        ⚠️ Importante: Realiza la transferencia y guarda tu comprobante. Tu boleto será activado automáticamente.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {selectedMethod === 'card' && (
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-4">
              {isLoading ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-sm text-[#6B7280]">Preparando formulario...</p>
                </div>
              ) : showPaymentDetails ? (
                <>
                  <h3 className="font-bold text-[#111827] mb-4">Datos de la tarjeta</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-2">
                        Número de tarjeta
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                          maxLength={19}
                          className="w-full px-4 py-3 pl-12 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                          placeholder="1234 5678 9012 3456"
                        />
                        <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-2">
                        Nombre del titular
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value.toUpperCase())}
                          className="w-full px-4 py-3 pl-12 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                          placeholder="NOMBRE APELLIDO"
                        />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-2">
                          Fecha de expiración
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={cardExpiry}
                            onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                            maxLength={5}
                            className="w-full px-4 py-3 pl-10 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="MM/AA"
                          />
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-2">
                          CVV
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={cardCvv}
                            onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            maxLength={4}
                            className="w-full px-4 py-3 pl-10 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                            placeholder="123"
                          />
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                        </div>
                      </div>
                    </div>

                    <div className="pt-2">
                      <div className="flex items-center gap-2 text-xs text-[#6B7280] mb-3">
                        <Lock className="w-4 h-4" />
                        <span>Tu información está protegida y encriptada</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {selectedMethod === 'link' && (
            <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-4">
              <h3 className="font-bold text-[#111827] mb-4">Link de pago generado</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-[#F7F8FA] rounded-[10px]">
                  <p className="text-xs text-[#6B7280] mb-2">Tu link de pago</p>
                  <div className="flex items-center gap-2">
                    <p className="flex-1 text-sm font-mono text-[#FF6A2A] break-all">
                      https://pay.mexmo.com/abc123xyz789
                    </p>
                    <button className="p-2 hover:bg-[#E5E7EB] rounded-[8px] transition-colors flex-shrink-0">
                      <Copy className="w-4 h-4 text-[#6B7280]" />
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[10px]">
                  <p className="text-xs text-[#FF6A2A] font-semibold mb-2">
                    📱 Cómo usar tu link de pago:
                  </p>
                  <ul className="text-xs text-[#6B7280] space-y-1 ml-4 list-disc">
                    <li>Copia el link</li>
                    <li>Compártelo o ábrelo en tu navegador</li>
                    <li>Completa el pago de forma segura</li>
                  </ul>
                </div>

                <div className="text-center">
                  <p className="text-sm font-semibold text-[#111827] mb-2">
                    Monto a pagar: ${finalPrice.toLocaleString('es-MX')} MXN
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom CTA */}
        {selectedMethod && (selectedMethod === 'card' || selectedMethod === 'link' || showPaymentDetails) && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E5E7EB] p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]">
            <button
              onClick={handlePayment}
              disabled={selectedMethod === 'card' && (!cardNumber || !cardName || !cardExpiry || !cardCvv)}
              className="w-full py-3.5 bg-[#FF6A2A] hover:bg-[#C2410C] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white font-semibold rounded-[12px] transition-colors flex items-center justify-center gap-2"
            >
              {selectedMethod === 'spei' ? 'Confirmar transferencia' : 'Pagar ahora'}
            </button>
          </div>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block min-h-screen bg-[#F7F8FA]">
        {/* Top Bar */}
        <div className="bg-white border-b border-[#E5E7EB] py-4">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goToPassengers}
                className="flex items-center gap-2 text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Volver</span>
              </button>
              <div className="border-l border-[#E5E7EB] pl-4">
                <h1 className="font-bold text-[#111827]">
{firstWord(safeBookingData?.departureLocation) || 'Origen'} → {firstWord(safeBookingData?.arrivalLocation) || 'Destino'}
                </h1>
                <p className="text-sm text-[#6B7280]">
                  {safeBookingData.departureTime} - {safeBookingData.arrivalTime} • {safeBookingData.duration}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-center">
              {steps.map((step, index) => {
                const isActive = step.id === 'payment';
                const isCompleted = step.completed;

                return (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                          isCompleted
                            ? 'bg-[#10B981] border-[#10B981]'
                            : isActive
                            ? 'bg-[#FF6A2A] border-[#FF6A2A]'
                            : 'bg-white border-[#E5E7EB]'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="w-6 h-6 text-white" />
                        ) : (
                          <CreditCard className={`w-6 h-6 ${isActive ? 'text-white' : 'text-[#6B7280]'}`} />
                        )}
                      </div>
                      <p className={`mt-2 text-sm font-medium ${isActive ? 'text-[#111827]' : 'text-[#6B7280]'}`}>
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`w-32 h-0.5 mx-4 mb-6 ${isCompleted ? 'bg-[#10B981]' : 'bg-[#E5E7EB]'}`}></div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-2">
              <p className="text-sm text-[#6B7280]">Paso 3 de 3</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-12 gap-8">
            {/* Left Column - Payment Methods */}
            <div className="col-span-8">
              <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6">
                <h2 className="text-xl font-bold text-[#111827] mb-6">Método de pago</h2>

                {/* Payment Methods Grid */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <button
                    onClick={() => handleMethodSelect('spei')}
                    className={`p-6 rounded-[12px] border-2 transition-all ${
                      selectedMethod === 'spei'
                        ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                        : 'border-[#E5E7EB] bg-white hover:border-[#FF6A2A]/30'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                        selectedMethod === 'spei' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                      }`}>
                        <Building2 className={`w-7 h-7 ${
                          selectedMethod === 'spei' ? 'text-white' : 'text-[#6B7280]'
                        }`} />
                      </div>
                      <h3 className="font-semibold text-[#111827] mb-1">SPEI</h3>
                      <p className="text-xs text-[#6B7280]">Transferencia</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleMethodSelect('card')}
                    className={`p-6 rounded-[12px] border-2 transition-all ${
                      selectedMethod === 'card'
                        ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                        : 'border-[#E5E7EB] bg-white hover:border-[#FF6A2A]/30'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                        selectedMethod === 'card' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                      }`}>
                        <CreditCard className={`w-7 h-7 ${
                          selectedMethod === 'card' ? 'text-white' : 'text-[#6B7280]'
                        }`} />
                      </div>
                      <h3 className="font-semibold text-[#111827] mb-1">Tarjeta</h3>
                      <p className="text-xs text-[#6B7280]">Visa/Mastercard</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleMethodSelect('link')}
                    className={`p-6 rounded-[12px] border-2 transition-all ${
                      selectedMethod === 'link'
                        ? 'border-[#FF6A2A] bg-[#FFF5F0]'
                        : 'border-[#E5E7EB] bg-white hover:border-[#FF6A2A]/30'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                        selectedMethod === 'link' ? 'bg-[#FF6A2A]' : 'bg-[#F7F8FA]'
                      }`}>
                        <LinkIcon className={`w-7 h-7 ${
                          selectedMethod === 'link' ? 'text-white' : 'text-[#6B7280]'
                        }`} />
                      </div>
                      <h3 className="font-semibold text-[#111827] mb-1">Link de pago</h3>
                      <p className="text-xs text-[#6B7280]">Paga en línea</p>
                    </div>
                  </button>
                </div>

                {/* Add Coupon Section */}
                <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-4 mb-4">
                  <h3 className="font-semibold text-[#111827] mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#FF6A2A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Añadir cupón
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      disabled={couponApplied}
                      className="flex-1 px-3 py-2.5 border border-[#E5E7EB] rounded-[10px] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent disabled:bg-[#F7F8FA] disabled:text-[#9CA3AF]"
                      placeholder="Ingresa tu cupón"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={couponApplied || !couponCode}
                      className="px-4 py-2.5 bg-[#FF6A2A] hover:bg-[#C2410C] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] text-white font-semibold rounded-[10px] text-sm transition-colors"
                    >
                      {couponApplied ? 'Aplicado' : 'Aplicar'}
                    </button>
                  </div>
                  {couponApplied && (
                    <div className="mt-3 p-2 bg-[#ECFDF5] border border-[#10B981] rounded-[8px] flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#10B981]" />
                      <span className="text-sm text-[#10B981] font-medium">
                        ¡Cupón aplicado! -${discount.toLocaleString('es-MX')} MXN
                      </span>
                    </div>
                  )}
                </div>

                {/* Payment Details - Same as mobile but with desktop styling */}
                {selectedMethod === 'spei' && (
                  <div className="mt-6">
                    {isLoading ? (
                      <div className="py-20 text-center">
                        <div className="w-16 h-16 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-[#6B7280]">Generando datos bancarios...</p>
                      </div>
                    ) : showPaymentDetails ? (
                      <div>
                        <h3 className="font-bold text-[#111827] mb-4">Datos para transferencia</h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-[#F7F8FA] rounded-[10px]">
                            <p className="text-sm text-[#6B7280] mb-1">Beneficiario</p>
                            <p className="font-semibold text-[#111827]">Mexmo S.A. de C.V.</p>
                          </div>

                          <div className="p-4 bg-[#F7F8FA] rounded-[10px]">
                            <p className="text-sm text-[#6B7280] mb-1">Banco</p>
                            <p className="font-semibold text-[#111827]">BBVA México</p>
                          </div>

                          <div className="col-span-2 p-4 bg-[#F7F8FA] rounded-[10px]">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-[#6B7280] mb-1">CLABE</p>
                                <p className="font-mono font-bold text-lg text-[#111827]">012180001234567890</p>
                              </div>
                              <button className="p-3 hover:bg-[#E5E7EB] rounded-[8px] transition-colors">
                                <Copy className="w-5 h-5 text-[#6B7280]" />
                              </button>
                            </div>
                          </div>

                          <div className="col-span-2 p-4 bg-[#FFF5F0] border-2 border-[#FF6A2A] rounded-[10px]">
                            <p className="text-sm text-[#6B7280] mb-1">Monto a transferir</p>
                            <p className="text-3xl font-bold text-[#FF6A2A]">${finalPrice.toLocaleString('es-MX')} MXN</p>
                          </div>

                          <div className="col-span-2 p-4 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[10px]">
                            <p className="text-sm text-[#FF6A2A] font-semibold">
                              ⚠️ Importante: Realiza la transferencia y guarda tu comprobante. Tu boleto será activado automáticamente.
                            </p>
                          </div>
                        </div>

                        <Button 
                          variant="primary" 
                          className="w-full mt-6 py-3"
                          onClick={handlePayment}
                        >
                          Confirmar transferencia
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )}

                {selectedMethod === 'card' && (
                  <div className="mt-6">
                    {isLoading ? (
                      <div className="py-12 text-center">
                        <div className="w-12 h-12 border-4 border-[#FF6A2A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-sm text-[#6B7280]">Preparando formulario...</p>
                      </div>
                    ) : showPaymentDetails ? (
                      <>
                        <h3 className="font-bold text-[#111827] mb-4">Datos de la tarjeta</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-[#111827] mb-2">
                              Número de tarjeta
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={cardNumber}
                                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                maxLength={19}
                                className="w-full px-4 py-3 pl-12 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                placeholder="1234 5678 9012 3456"
                              />
                              <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-[#111827] mb-2">
                              Nombre del titular
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={cardName}
                                onChange={(e) => setCardName(e.target.value.toUpperCase())}
                                className="w-full px-4 py-3 pl-12 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                placeholder="NOMBRE APELLIDO"
                              />
                              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-[#111827] mb-2">
                                Fecha de expiración
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={cardExpiry}
                                  onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                                  maxLength={5}
                                  className="w-full px-4 py-3 pl-11 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                  placeholder="MM/AA"
                                />
                                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-[#111827] mb-2">
                                CVV
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={cardCvv}
                                  onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                  maxLength={4}
                                  className="w-full px-4 py-3 pl-11 border border-[#E5E7EB] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#FF6A2A] focus:border-transparent"
                                  placeholder="123"
                                />
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-sm text-[#6B7280] pt-2">
                            <Lock className="w-4 h-4" />
                            <span>Tu información está protegida y encriptada</span>
                          </div>

                          <Button 
                            variant="primary" 
                            className="w-full mt-4 py-3"
                            onClick={handlePayment}
                            disabled={!cardNumber || !cardName || !cardExpiry || !cardCvv}
                          >
                            Pagar ${finalPrice.toLocaleString('es-MX')} MXN
                          </Button>
                        </div>
                      </>
                    ) : null}
                  </div>
                )}

                {selectedMethod === 'link' && (
                  <div className="mt-6">
                    <h3 className="font-bold text-[#111827] mb-4">Link de pago generado</h3>
                    
                    <div className="space-y-4">
                      <div className="p-5 bg-[#F7F8FA] rounded-[10px]">
                        <p className="text-sm text-[#6B7280] mb-2">Tu link de pago</p>
                        <div className="flex items-center gap-3">
                          <p className="flex-1 text-base font-mono text-[#FF6A2A] break-all">
                            https://pay.mexmo.com/abc123xyz789
                          </p>
                          <button className="p-3 hover:bg-[#E5E7EB] rounded-[8px] transition-colors">
                            <Copy className="w-5 h-5 text-[#6B7280]" />
                          </button>
                        </div>
                      </div>

                      <div className="p-4 bg-[#FFF5F0] border border-[#FF6A2A] rounded-[10px]">
                        <p className="text-sm text-[#FF6A2A] font-semibold mb-2">
                          📱 Cómo usar tu link de pago:
                        </p>
                        <ul className="text-sm text-[#6B7280] space-y-1 ml-5 list-disc">
                          <li>Copia el link</li>
                          <li>Compártelo o ábrelo en tu navegador</li>
                          <li>Completa el pago de forma segura</li>
                        </ul>
                      </div>

                      <div className="text-center p-4 bg-[#FFF5F0] rounded-[10px]">
                        <p className="text-sm text-[#6B7280] mb-1">Monto a pagar</p>
                        <p className="text-2xl font-bold text-[#FF6A2A]">
                          ${finalPrice.toLocaleString('es-MX')} MXN
                        </p>
                      </div>

                      <Button 
                        variant="primary" 
                        className="w-full mt-4 py-3"
                        onClick={handlePayment}
                      >
                        Confirmar link de pago
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="col-span-4">
              <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 sticky top-6">
                <h3 className="font-bold text-[#111827] mb-4">Resumen de compra</h3>

                <div className="space-y-4 pb-4 border-b border-[#E5E7EB]">
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Compañía</p>
                    <p className="font-semibold text-[#111827]">{safeBookingData.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Ruta</p>
                    <p className="font-semibold text-[#111827]">
                 {firstWord(safeBookingData?.departureLocation) || 'Origen'} →{' '}
{firstWord(safeBookingData?.arrivalLocation) || 'Destino'}

                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Asientos</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {safeSelectedSeats.map((seat) => (
                        <span
                          key={seat}
                          className="px-3 py-1.5 bg-[#1E3A8A] text-white text-sm font-bold rounded-[8px]"
                        >
                          {seat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[#6B7280] mb-1">Pasajeros</p>
                    <p className="font-semibold text-[#111827]">{safeSelectedSeats.length} persona{safeSelectedSeats.length > 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div className="pt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#6B7280]">Subtotal</span>
                    <span className="font-semibold text-[#111827]">
                      ${totalPrice.toLocaleString('es-MX')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm pb-4 border-b border-[#E5E7EB]">
                    <span className="text-[#6B7280]">Impuestos</span>
                    <span className="font-semibold text-[#111827]">$0</span>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-bold text-[#111827]">Total</span>
                    <span className="text-2xl font-bold text-[#111827]">
                      ${finalPrice.toLocaleString('es-MX')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentForm;